import { LayerBuildingRooms } from '@/components/map/layers/mapview-layers'
import LayerLevel from '@/components/map/layers/mapview-layers/LayerLevel.vue'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export interface Layer {
  id: string
  name: string
  visible: boolean
  opacity: number
  order: number
  component: any
}

export interface LayerWithBefore extends Layer {
  before: string
}

const defaultLayers: Omit<Layer, 'order'>[] = [
  {
    id: 'spaces-3d',
    name: '3D Spaces',
    visible: false,
    opacity: 1,
    component: LayerBuildingRooms
  },
  {
    id: 'levels',
    name: 'Levels',
    visible: false,
    opacity: 1,
    component: LayerLevel
  }
]

export const useLayers = defineStore('layers', () => {
  const layers = ref<Record<string, Layer | undefined>>({})
  const orderedLayers = computed<LayerWithBefore[]>(() =>
    Object.values(layers.value)
      .filter((x) => x !== undefined)
      .map((x) => x!)
      .sort((a, b) => a.order - b.order)
      .map((layer, index, array) => ({
        ...layer,
        before: array[index + 1]?.id
      }))
  )

  function addLayer(layer: Layer) {
    layers.value = {
      ...layers.value,
      [layer.id]: layer
    }
  }

  function modifyLayer(id: string, modifier: (layer: Layer) => Layer) {
    const layer = layers.value[id]
    if (!layer) throw new Error(`Layer ${id} not found`)
    layers.value = {
      ...layers.value,
      [id]: modifier(layer)
    }
  }

  function hideLayer(id: string) {
    modifyLayer(id, (layer) => ({
      ...layer,
      visible: false
    }))
  }

  function showLayer(id: string) {
    modifyLayer(id, (layer) => ({
      ...layer,
      visible: true
    }))
  }

  function toggleLayer(id: string) {
    modifyLayer(id, (layer) => ({
      ...layer,
      visible: !layer.visible
    }))
  }

  function setLayerOpacity(id: string, opacity: number) {
    modifyLayer(id, (layer) => ({
      ...layer,
      opacity
    }))
  }

  function setLayerOrder(id: string, order: number) {
    modifyLayer(id, (layer) => ({
      ...layer,
      order
    }))
  }

  defaultLayers.forEach((layer, index) => {
    addLayer({ ...layer, order: index })
  })

  return {
    addLayer,
    hideLayer,
    showLayer,
    toggleLayer,
    setLayerOpacity,
    setLayerOrder,
    orderedLayers
  }
})
